import { Injectable } from '@angular/core';

import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

export interface StoredTokens {
  accessToken: string;
  refreshToken: string | null;
  idToken: string | null;
  expiresAt: number;
  refreshExpiresAt: number | null;
}

@Injectable({ providedIn: 'root' })
export class KeycloakTokenStore {
  async load(clientId: string): Promise<StoredTokens | null> {
    const raw = await this.read(this.key(clientId));
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as StoredTokens;
    } catch {
      return null;
    }
  }

  async save(clientId: string, tokens: StoredTokens): Promise<void> {
    await this.write(this.key(clientId), JSON.stringify(tokens));
  }

  async clear(clientId: string): Promise<void> {
    await this.remove(this.key(clientId));
  }

  private key(clientId: string): string {
    return `keycloak.session.${clientId}`;
  }

  private async read(key: string): Promise<string | null> {
    if (!Capacitor.isNativePlatform()) {
      return localStorage.getItem(key);
    }
    try {
      const { value } = await SecureStoragePlugin.get({ key });
      return value;
    } catch {
      return null;
    }
  }

  private async write(key: string, value: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      localStorage.setItem(key, value);
      return;
    }
    await SecureStoragePlugin.set({ key, value });
  }

  private async remove(key: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      localStorage.removeItem(key);
      return;
    }
    try {
      await SecureStoragePlugin.remove({ key });
    } catch {
      return;
    }
  }
}
